import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import clsx from 'clsx'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscription'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import { EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_WATER_TRANSFER_ID, EXP_CATEGORY_PRIVATE_TRANSFER_ID, EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW, EXP_CATEGORY_PHOTOSHOOT_TRAVELSHOOT_ID } from 'constants/experience'
import NestableClickable from 'components/Common/NestableClickable'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import ExperienceGiveAsGiftInfoText from 'components/Experiences/ExperienceGiveAsGiftModal/ExperienceGiveAsGiftInfoText'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import config from 'constants/config'
import Typography from 'LuxKit/Typography/Typography'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { useInView } from 'react-intersection-observer'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import ExperienceCategoryLabel from './ExperienceCategoryLabel'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ExperienceTilePrices from './ExperienceTilePrices'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import NumberRating from 'components/Common/NumberRating'
import { isExperienceOfferRatingDisplayable } from 'lib/order/reviewUtils'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import { getAccommodationTagIds } from 'checkout/selectors/experienceSelectors'
import ExperienceUrgencyTag from './ExperienceUrgencyTag'
import { getExperienceTileSaleUnit } from 'lib/experiences/getExperienceTileSaleUnit'

const ExperienceImageContainer = styled.div`
  position: relative;
  height: ${rem(162)};
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const CheckoutUpsellVariantPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: ${rem(2)};
`

const CheckoutUpsellVariantPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${rem(4)} ${rem(4)};
`

const CheckoutUpsellVariantPrice = styled(PriceRowPrice)`
  column-gap: 0;

  > *:first-child {
    font-weight: ${props => props.theme.font.primary.weight.superBold};
  }
`

const CheckoutUpsellVariantLuxPlusPrice = styled(PriceRowLuxPlusPrice)`
  column-gap: 0;

  > *:first-child {
    font-size: 14px;
    line-height: 20px;
    font-weight: ${props => props.theme.font.primary.weight.superBold};
  }
`

const CheckoutUpsellVariantLoyalty = styled.div`
  svg {
    width: ${rem(14)};
    height: ${rem(14)};
  }
`

const CheckoutUpsellVariantRating = styled.div`
  position: absolute;
  top: ${rem(4)};
  inset-inline-end: ${rem(4)};
  z-index: 1;
`

const BadgeContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  inset-inline-start: ${rem(8)};
  inset-inline-end: ${rem(8)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(2)};
`

const Tile = styled(NestableClickable)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(364)};
  }

  &.tag-assigned {
    border: 2px solid ${props => props.theme.palette.highlight.primary.darkBackground};
  }

  &.checkout-upsell-variant {
    display: grid;
    grid-template-columns: ${rem(258)} minmax(0, 1fr);
    gap: ${rem(24)};
    border: none;
    border-radius: 0;
    background-color: transparent;
    overflow: visible;
    min-height: unset;

    ${ExperienceImageContainer} {
      height: ${rem(160)};
      background-color: transparent;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    ${mediaQueryDown.mobile} {
      grid-template-columns: ${rem(132)} minmax(0, 1fr);
      gap: ${rem(14)};

      ${ExperienceImageContainer} {
        height: ${rem(96)};
        min-height: unset;
      }
    }
  }
`

const TileContent = styled.div`
  position: relative;
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;

  > * + * {
    margin-top: ${rem(8)};
  }

  &.checkout-upsell-variant {
    padding: 0;
    gap: 0;
    justify-content: space-between;
    flex-grow: unset;
    min-height: ${rem(160)};

    > * + * {
      margin-top: 0;
    }

    ${mediaQueryDown.mobile} {
      min-height: ${rem(96)};
    }
  }
`

const DistanceText = styled(Typography)`
  &.checkout-upsell-variant {
    ${mediaQueryDown.mobile} {
      display: none;
    }
  }
`

const RightSideBadge = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
`

const ExperienceAddedBadge = styled(Group)`
  background-color: ${props => props.theme.palette.messaging.success.lightBackground};
  padding: ${rem(4)};
`

const UrgencyBadgeContainer = styled.div`
  position: absolute;
  bottom: ${rem(8)};
  inset-inline-start: ${rem(8)};
  z-index: 1;
`

const TRANSFER_AND_LOUNGE_CATEGORIES = [
  EXP_CATEGORY_AIRPORT_TRANSFER_ID,
  EXP_CATEGORY_AIRPORT_LOUNGE_ID,
  EXP_CATEGORY_WATER_TRANSFER_ID,
  EXP_CATEGORY_PRIVATE_TRANSFER_ID,
  EXP_CATEGORY_PHOTOSHOOT_TRAVELSHOOT_ID,
]

interface Props {
  experience: App.ExperienceBookingView
  partnershipBadge?: boolean
  onViewDetails?: (experience: App.ExperienceBookingView) => void
  to?: string
  isStandaloneExperience?: boolean
  className?: string
  position?: number
  isLuxPlusEnabled?: boolean
  hasLuxPlusOrSubscriptionInCart?: boolean
  tagIds?: Array<string>
  listKey?: string
  offerMetadata?: App.ExperienceOffersMetadata[string]
  presentation?: 'default' | 'checkoutUpsellVariant'
}

function ExperienceTile(props: Props) {
  const {
    className,
    experience,
    onViewDetails,
    to,
    isStandaloneExperience = true,
    isLuxPlusEnabled = false,
    hasLuxPlusOrSubscriptionInCart = false,
    position,
    tagIds,
    listKey,
    offerMetadata: offerMetadataProp,
    presentation = 'default',
  } = props
  const isCheckoutUpsellVariant = presentation === 'checkoutUpsellVariant'

  const offerMetadataFromState = useAppSelector(state => listKey ? state.experience.experienceOffersMetadata[listKey]?.[experience.id] : undefined)
  const offerMetadata = offerMetadataProp ?? offerMetadataFromState

  const intl = useIntl()
  const trackEvent = useTrackExperienceOfferEvent()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  const originalExperience = experience.originalExperience

  const onViewDetailsClick = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: originalExperience,
        position,
        key: EventDataKey.ProductClick,
      })
    }

    trackEvent('OFFER_TILE_CLICKED', [{
      type: 'click',
      data: {
        offer: originalExperience,
      },
    }])
    onViewDetails?.(experience)
  }, [experience, originalExperience, dispatchOfferListEvent, onViewDetails, position, trackEvent])

  useEffect(() => {
    if (inView && imageLoaded && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: originalExperience,
        position,
        key: EventDataKey.ProductImpression,
      })

      trackEvent('OFFER_TILE_IMPRESSION', [{
        type: 'impression',
        data: {
          offer: originalExperience,
        },
      }])
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- Only
  }, [inView, imageLoaded])

  const tripId = useAppSelector(getImmersiveTripId)
  const linkParams = useMemo(() => {
    const updatedTo = (to && config.OPEN_NEW_TAB_OFFER_CLICK && tripId) ?
        addQueryParamsToPath(to, { tripId }) :
      to

    return {
      onClick: onViewDetailsClick,
      to: updatedTo,
      ...(config.OPEN_NEW_TAB_OFFER_CLICK ? { target: '_blank' } : {}),
    }
  }, [tripId, to, onViewDetailsClick])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorExperienceOptions(originalExperience, {
      memberPrice: isLuxPlusEnabled && hasLuxPlusOrSubscriptionInCart ? originalExperience.pricing.memberPrice : 0,
    }),
  ], [originalExperience, isLuxPlusEnabled, hasLuxPlusOrSubscriptionInCart])

  const isTransferOrLounge = experience.categories.some(category =>
    TRANSFER_AND_LOUNGE_CATEGORIES.includes(category.id),
  )

  const showDistanceText = useMemo(() => {
    return experience.distance && !isTransferOrLounge
  }, [experience.distance, isTransferOrLounge])

  const showLuxPlusMemberPrice = useMemo(() => {
    // show for standalone experiences if feature flag is enabled
    if (isStandaloneExperience && isLuxPlusEnabled && experience.pricing.memberPrice > 0) return true

    // show on in flow if user has lux plus or subscription in cart
    if (!isStandaloneExperience && isLuxPlusEnabled && hasLuxPlusOrSubscriptionInCart && experience.pricing.memberPrice > 0) return true

    return false
  }, [isStandaloneExperience, isLuxPlusEnabled, hasLuxPlusOrSubscriptionInCart, experience.pricing.memberPrice])

  const showDiscount = experience.pricing.discountPercent >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && (isStandaloneExperience || !showLuxPlusMemberPrice)

  const { totalMemberPrice, totalSalePrice, totalGrossPrice, memberDiscountPercentage } = useMemo(() => {
    const totalMemberPrice = experience.cartData ? experience.pricing.memberPrice * experience.cartData.count : experience.pricing.memberPrice
    const totalSalePrice = experience.cartData?.totalPrice ?? experience.price
    const totalGrossPrice = experience.cartData ? (experience.cartData?.totalGrossPrice) : experience.pricing.value

    return {
      totalMemberPrice: (isStandaloneExperience || hasLuxPlusOrSubscriptionInCart) ? totalMemberPrice : undefined,
      totalSalePrice: (isStandaloneExperience || !hasLuxPlusOrSubscriptionInCart || !totalMemberPrice) ? totalSalePrice : undefined,
      totalGrossPrice: (!isStandaloneExperience && hasLuxPlusOrSubscriptionInCart) ? totalGrossPrice : undefined,
      memberDiscountPercentage: (!experience.cartData?.count) ? experience.pricing.memberDiscountPercentage : undefined,
    }
  }, [experience, isStandaloneExperience, hasLuxPlusOrSubscriptionInCart])

  const hasTagAssigned = useMemo(() => {
    return tagIds?.some(tagId => experience.tagIds?.includes(tagId))
  }, [tagIds, experience.tagIds])

  const editLabel = intl.formatMessage({
    id: '5agUUZ5osThBLWohVwti',
    defaultMessage: 'Edit',
    description: 'Button text to edit an experience already added to cart',
  })
  const viewLabel = intl.formatMessage({
    id: 'IZisqxEC06wyEPpzbzQt',
    defaultMessage: 'View',
    description: 'Button text to view experience offer details for the experience',
  })
  const fromLabel = intl.formatMessage({
    id: 'EAQ_GV7KRwveLWB08EmL',
    defaultMessage: 'From',
    description: 'Price caption prefix on the experience tile prices',
  })
  const totalLabel = intl.formatMessage({
    id: 'whMf0sOzbI-AWwhc2zR7',
    defaultMessage: 'total',
    description: 'Sale unit label for total price on experience tile prices',
  })
  const ticketUnitLabel = experience.ticketUnitLabel ?? 'person'
  const checkoutUpsellVariantTicketUnitLabel = experience.ticketUnitLabel || 'person'
  const saleUnitLabel = getExperienceTileSaleUnit({
    isFromPrice: !experience.cartData,
    ticketUnitLabel: checkoutUpsellVariantTicketUnitLabel,
    totalLabel,
  })
  const checkoutUpsellVariantShowsMemberPrice = showLuxPlusMemberPrice && totalMemberPrice != null
  const checkoutUpsellVariantPrice = checkoutUpsellVariantShowsMemberPrice ? totalMemberPrice : totalSalePrice

  return (
    <Tile
      {...linkParams}
      as={to ? ClickableLink : undefined}
      data-testid="experience-tile"
      className={clsx(className, {
        'checkout-upsell-variant': isCheckoutUpsellVariant,
        'tag-assigned': hasTagAssigned && !isCheckoutUpsellVariant,
      })}
    >
      <ExperienceImageContainer>
        {experience.image && <Image
          height={isCheckoutUpsellVariant ? 180 : 126}
          image={experience.image}
          fit="center"
          dpr={2}
          onLoad={() => setImageLoaded(true)}
        />}
        {!isCheckoutUpsellVariant && <BadgeContainer>
          <div>
            <LabelGroup>
              {hasTagAssigned && <ContentTagLabels offerId={experience.originalExperience.id} page="search" />}
              {!hasTagAssigned && <ExperienceCategoryLabel categories={experience.categories} />}
            </LabelGroup>
          </div>
          <TripGuard>
            <ExperienceBookmarkButton offer={originalExperience} withLabel="never" size="small" />
          </TripGuard>
        </BadgeContainer>}
        {!isCheckoutUpsellVariant && config.EXPERIENCE_BADGE_IMAGE_ID && <RightSideBadge>
          <Image id={config.EXPERIENCE_BADGE_IMAGE_ID} height={80} width={80} />
        </RightSideBadge>}
        {!isCheckoutUpsellVariant && <UrgencyBadgeContainer>
          <ExperienceUrgencyTag urgencyTags={offerMetadata?.urgencyTags} experience={experience.originalExperience} />
        </UrgencyBadgeContainer>}
        {isCheckoutUpsellVariant && config.EXPERIENCES_REVIEWS_ENABLED && isExperienceOfferRatingDisplayable(experience) && <CheckoutUpsellVariantRating>
          <NumberRating
            rating={experience.rating?.score}
            total={experience.rating?.reviewsTotal}
            variant="xs"
            numberPosition="start"
            onlyNumber
          />
        </CheckoutUpsellVariantRating>}
      </ExperienceImageContainer>
      {!!experience.cartData && !isCheckoutUpsellVariant && <ExperienceAddedBadge direction="horizontal" gap={2} horizontalAlign="center">
        <LineCheckCircleIcon size="XS" />
        <CaptionTextBlock variant="medium" weight="normal" colour="light-foreground-alt">
          {intl.formatMessage(
            {
              id: 'BU0X5V7wGeD0B1aq6Cuc',
              defaultMessage: '{count}x added',
              description: 'Label showing how many times an experience has been added to cart',
            },
            { count: experience.cartData.count },
          )}
        </CaptionTextBlock>
      </ExperienceAddedBadge>}
      <TileContent ref={inViewRef} className={clsx({ 'checkout-upsell-variant': isCheckoutUpsellVariant })}>
        <div>
          <VerticalSpacer gap={isCheckoutUpsellVariant ? 0 : 8}>
            {!isCheckoutUpsellVariant && <Group direction="horizontal" gap={4} verticalAlign="center">
              <ExperienceAppDiscountTag experienceOffer={originalExperience} />
              <AgentHubAdditionalCommissionLabel offerId={originalExperience?.id} />
            </Group>}
            <Group direction="horizontal" verticalAlign="start" horizontalAlign="space-between" gap={4}>
              <VerticalSpacer gap={isCheckoutUpsellVariant ? 0 : 2}>
                <Typography wrap="pretty">
                  {experience.compactLocation && <Caption as="span" variant={isCheckoutUpsellVariant ? 'medium' : 'large'} weight="normal">{experience.compactLocation}</Caption>}
                </Typography>
                {showDistanceText && <DistanceText wrap="pretty" className={clsx({ 'checkout-upsell-variant': isCheckoutUpsellVariant })}>
                  <Caption as="span" variant="large" colour="light-foreground-alt" weight="normal">
                    <FormattedMessage
                      id="C_2Le5x9iSfeQHNV2_vv"
                      defaultMessage="{distance} km from hotel"
                      description="Distance from hotel for an experience, e.g. '2.5 km from hotel'"
                      values={{
                        distance: <FormatDecimal maximumFractionDigits={experience.distance && experience.distance > 1 ? 0 : 1} value={experience.distance ?? 0} />,
                      }}
                    />
                  </Caption>
                </DistanceText>}
              </VerticalSpacer>
              {!isCheckoutUpsellVariant && config.EXPERIENCES_REVIEWS_ENABLED && isExperienceOfferRatingDisplayable(experience) && <NumberRating
                rating={experience.rating?.score}
                total={experience.rating?.reviewsTotal}
                variant="xs"
                numberPosition="start"
                onlyNumber
              />}
            </Group>
            <BodyText title={experience.title} lineClamp={isCheckoutUpsellVariant ? 2 : 3} variant={isCheckoutUpsellVariant ? 'small' : 'medium'} weight="bold">{experience.title}</BodyText>
            {originalExperience?.cancellationPolicies.length > 0 && isStandaloneExperience && <ExperienceCancellationText
              cancellationInfo={originalExperience?.copy.cancellationInfo}
            />}
            {!!originalExperience.giftType && isStandaloneExperience && <ExperienceGiveAsGiftInfoText experience={originalExperience} />}
            {!isCheckoutUpsellVariant && <LoyaltyPointsEarn
              calculationRequests={pointsEarnCalculationRequests}
              calculationType="estimate"
              offer={originalExperience}
              hideStatusCredits
            />}
          </VerticalSpacer>
        </div>
        <VerticalSpacer gap={4} as={isCheckoutUpsellVariant ? CheckoutUpsellVariantPriceContainer : undefined}>
          {isCheckoutUpsellVariant && <CheckoutUpsellVariantLoyalty>
            <LoyaltyPointsEarn
              calculationRequests={pointsEarnCalculationRequests}
              calculationType="estimate"
              offer={originalExperience}
              hideInfoIcon
              hideStatusCredits
            />
          </CheckoutUpsellVariantLoyalty>}
          {showDiscount && !isCheckoutUpsellVariant && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={experience.pricing.discountPercent}
          />}
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={4}>
            <div>
              {isCheckoutUpsellVariant && checkoutUpsellVariantPrice != null ?
                  <CheckoutUpsellVariantPriceRow>
                    {!experience.cartData && <Caption as="span" variant="medium" colour="light-foreground-alt" weight="normal">
                      {fromLabel}
                    </Caption>}
                    {checkoutUpsellVariantShowsMemberPrice ?
                        <CheckoutUpsellVariantLuxPlusPrice
                          memberPrice={checkoutUpsellVariantPrice}
                          saleUnit={saleUnitLabel}
                          size="S"
                        /> :
                        <CheckoutUpsellVariantPrice
                          price={checkoutUpsellVariantPrice}
                          priceStyle="body-text"
                          saleUnit={saleUnitLabel}
                          size="S"
                        />}
                  </CheckoutUpsellVariantPriceRow> :
                  <ExperienceTilePrices
                    totalPrice={totalSalePrice}
                    totalMemberPrice={totalMemberPrice}
                    totalGrossPrice={totalGrossPrice}
                    isFromPrice={!experience.cartData}
                    ticketUnitLabel={ticketUnitLabel}
                    discountMemberPercentage={memberDiscountPercentage}
                  />}
              <PriceRowAgentHubCommission
                offerId={experience.id}
                offer={experience.originalExperience}
                productType="experience"
                offerLocations={experience.location ? [experience.location] : undefined}
              />
            </div>
            {!isCheckoutUpsellVariant && <TextButton kind="secondary" nonInteractable startIcon={experience.cartData ? <LineEditAltIcon /> : undefined}>
              {experience.cartData ? editLabel : viewLabel}
            </TextButton>}
          </Group>
        </VerticalSpacer>
      </TileContent>
    </Tile>
  )
}

const mapStateToProps = (state: App.State) => {
  return {
    tagIds: getAccommodationTagIds(state),
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
    hasLuxPlusOrSubscriptionInCart: getHasLuxPlusOrSubscriptionInCart(state),
  }
}

export default connect(mapStateToProps)(ExperienceTile)
